import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { createPromptPreset, listPromptPresets } from 'tripPlanner/api'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { PromptModalResult } from '../../PromptModal'
import type { PromptOption } from '../../PromptSelector'

interface StoredPrompt {
  name: string
  promptId: string
  notes?: string
}

const DEFAULT_PROMPT_KEY = 'current'

interface UsePromptManagementArgs {
  canManagePrompts: boolean
}

interface UsePromptManagementReturn {
  promptOptions: Array<PromptOption>
  selectedPromptKey: string
  selectedPrompt: PromptOption
  loadPrompts: () => Promise<void>
  handlePromptAdd: (result: PromptModalResult) => Promise<boolean>
  handlePromptSelect: (key: string) => void
}

export function usePromptManagement({ canManagePrompts }: UsePromptManagementArgs): UsePromptManagementReturn {
  const intl = useIntl()
  const [customPrompts, setCustomPrompts] = useState<Array<StoredPrompt>>([])
  const [selectedPromptKey, setSelectedPromptKey] = useState<string>(DEFAULT_PROMPT_KEY)

  const DEFAULT_PROMPT_OPTION: PromptOption = useMemo(() => ({
    key: DEFAULT_PROMPT_KEY,
    name: intl.formatMessage({
      id: 'sme9U_dcbUHU7cLY4CrP',
      defaultMessage: 'Master',
      description: 'Default prompt preset name in the trip planner prompt selector',
    }),
    isDefault: true,
  }), [intl])

  const promptOptions = useMemo<Array<PromptOption>>(
    () => [
      DEFAULT_PROMPT_OPTION,
      ...customPrompts.map(p => ({
        key: p.promptId,
        name: p.name,
        promptId: p.promptId,
        notes: p.notes,
      })),
    ],
    [customPrompts, DEFAULT_PROMPT_OPTION],
  )

  const selectedPrompt = useMemo(
    () => promptOptions.find(option => option.key === selectedPromptKey) ?? DEFAULT_PROMPT_OPTION,
    [promptOptions, selectedPromptKey, DEFAULT_PROMPT_OPTION],
  )

  const getPromptErrorMessage = useCallback((error: unknown, fallback: string) => {
    const errorObj = error as { response?: { data?: { message?: string } }; message?: string } | undefined
    const message = errorObj?.response?.data?.message ?? errorObj?.message
    if (typeof message === 'string' && message.toLowerCase().includes('prompt id already exists')) {
      return intl.formatMessage({
        id: 'LOhdQe0HB9m9gCcEGNOD',
        defaultMessage: 'Prompt ID already exists',
        description: 'Error message when trying to create a prompt preset with a duplicate ID',
      })
    }
    return fallback
  }, [intl])

  const loadPrompts = useCallback(async() => {
    if (!canManagePrompts) {
      setCustomPrompts([])
      return
    }
    try {
      const presets = await listPromptPresets()
      setCustomPrompts(
        presets.map(preset => ({
          name: preset.name,
          promptId: preset.promptId,
          notes: preset.notes ?? undefined,
        })),
      )
    } catch {
      setCustomPrompts([])
    }
  }, [canManagePrompts])

  const handlePromptAdd = useCallback(async(result: PromptModalResult): Promise<boolean> => {
    if (!canManagePrompts) {
      return false
    }
    const { name, promptId, notes } = result
    try {
      await createPromptPreset({ name, promptId, notes })
      setCustomPrompts(prev => [...prev, { name, promptId, notes }])
      showSnackbar(intl.formatMessage({
        id: 'r7WPtOOOuy9uPnV5AIfV',
        defaultMessage: 'Prompt preset created',
        description: 'Success snackbar message after creating a prompt preset',
      }), 'success')
      return true
    } catch(error) {
      const message = getPromptErrorMessage(error, intl.formatMessage({
        id: 'aY8sAu5woz_gkzoQUYo8',
        defaultMessage: 'Failed to create prompt preset',
        description: 'Error snackbar message when creating a prompt preset fails',
      }))
      showSnackbar(message, 'critical')
      return false
    }
  }, [canManagePrompts, intl, getPromptErrorMessage])

  const handlePromptSelect = useCallback((key: string) => {
    setSelectedPromptKey(key)
  }, [])

  return {
    promptOptions,
    selectedPromptKey,
    selectedPrompt,
    loadPrompts,
    handlePromptAdd,
    handlePromptSelect,
  }
}
